import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TransactionAmount } from 'src/types';
import { blockchain } from 'src/api/blockchain';
import {
  weiToEthBigInt,
  isMongoLong,
  safeConvertToNumber,
} from 'src/utils/number';
import { EVMTransactionAddresses } from './components/EVMTransactionAddresses';
import { EVMTransactionDetails } from './components/EVMTransactionDetails';
import { EVMTransactionHeader } from './components/EVMTransactionHeader';
import { EVMTransactionInfo } from './components/EVMTransactionInfo';
import { useChainAndTxHashParams } from 'src/hooks/routing/useChainAndTxHashParams';

export const TxNative = () => {
  const { chain, txHash } = useChainAndTxHashParams();
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    data: transaction,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['transaction', chain, txHash],
    queryFn: async () => {
      if (!chain || !txHash) {
        throw new Error('Missing chain or transaction hash');
      }
      return await blockchain.getEVMTxByHash(chain, txHash);
    },
    enabled: Boolean(chain && txHash),
  });

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatAmount = (amount: number | TransactionAmount) => {
    if (typeof amount === 'number' || isMongoLong(amount)) {
      const amountNumber = safeConvertToNumber(amount);
      const ethAmount = weiToEthBigInt(amountNumber);
      return `${parseFloat(ethAmount)} ETH`;
    }
    return '0 ETH';
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <div className="mb-4 text-xl text-red-500">
          {error instanceof Error ? error.message : 'Transaction not found'}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      <div className="rounded-lg bg-white p-6 shadow-lg">
        <EVMTransactionHeader chain={chain} />
        <div className="space-y-4">
          <EVMTransactionInfo
            transaction={transaction}
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <EVMTransactionAddresses
            transaction={transaction}
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <EVMTransactionDetails
            transaction={transaction}
            formatAmount={formatAmount}
          />
        </div>
      </div>
    </div>
  );
};
